import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Search, AlertCircle, Sparkles, Stethoscope, UserRoundSearch } from 'lucide-react';
import GlobalLoader from './GlobalLoader';
import DoctorCard from './DoctorCard';
import DoctorScheduleModal from './DoctorScheduleModal';
import scheduleService from '../services/scheduleService';
import { doctorService } from '../services/doctorService';

// ─────────────────────────────────────────────────────────────────────────────
// ConditionSelection — modo "Agendar por condición"
//
// Réplica del flujo Flutter `ScheduleByConditionUnifiedPage`. El usuario elige
// una o más condiciones (categorías) y se llama al endpoint unificado
// `dais.professional.process_get_professional_by_categories` para listar
// doctoras que tratan esas condiciones para la especialidad activa.
//
// Diseño preserva colores Daisies, cards redondeados y press-scale.
// ─────────────────────────────────────────────────────────────────────────────

// Mapa por defecto de condiciones por especialidad (id de Odoo).
// Se usa cuando el backend no devuelve la lista de categorías. Idéntico en
// espíritu a lo que mostraba la app Flutter para arranque rápido del feature.
const FALLBACK_CONDITIONS_BY_SPECIALTY = {
  // Psicología
  16: [
    { id: 1, name: 'Ansiedad' },
    { id: 2, name: 'Depresión' },
    { id: 3, name: 'Estrés' },
    { id: 4, name: 'Duelo' },
    { id: 5, name: 'Problemas de pareja' },
    { id: 6, name: 'Autoestima' },
    { id: 7, name: 'Trauma' },
    { id: 8, name: 'Familia' },
  ],
  // Ginecología
  15: [
    { id: 11, name: 'Embarazo' },
    { id: 12, name: 'Anticoncepción' },
    { id: 13, name: 'Menopausia' },
    { id: 14, name: 'Ciclo menstrual' },
    { id: 15, name: 'ITS' },
    { id: 16, name: 'PCOS' },
  ],
  // Nutrición
  17: [
    { id: 21, name: 'Pérdida de peso' },
    { id: 22, name: 'Diabetes' },
    { id: 23, name: 'Embarazo' },
    { id: 24, name: 'Deportiva' },
    { id: 25, name: 'Vegetariana' },
  ],
  // Salud general
  18: [
    { id: 31, name: 'Tiroides' },
    { id: 32, name: 'Hipertensión' },
    { id: 33, name: 'Diabetes' },
    { id: 34, name: 'Cardíaco' },
    { id: 35, name: 'Digestivo' },
  ],
};

const ConditionSelection = ({
  onSelect,
  onBack,
  selectedSpecialty,
  loggedInUser,
  onOndemandRequest,
}) => {
  const [phase, setPhase] = useState('conditions'); // 'conditions' | 'doctors'
  const [selectedConditionIds, setSelectedConditionIds] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleModalContext, setScheduleModalContext] = useState(null);
  const [categories, setCategories] = useState([]);

  const specialtyId = selectedSpecialty?.id;

  // 1. Cargar categorías para la especialidad.
  // Intenta el endpoint del backend; si no devuelve, usa el fallback hardcodeado.
  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      if (!specialtyId) return;
      const remote = await scheduleService.getCategoriesBySpecialty({ specialtyId });
      if (cancelled) return;
      if (Array.isArray(remote) && remote.length > 0) {
        // Normalize: {id, name}
        const normalized = remote
          .map(c => ({ id: c.id ?? c.category_id, name: c.name || c.label || c.title }))
          .filter(c => c.id && c.name);
        if (normalized.length > 0) {
          setCategories(normalized);
          return;
        }
      }
      setCategories(FALLBACK_CONDITIONS_BY_SPECIALTY[specialtyId] || []);
    };
    loadCategories();
    return () => { cancelled = true; };
  }, [specialtyId]);

  const toggleCondition = (id) => {
    setSelectedConditionIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSearch = async () => {
    if (!specialtyId || selectedConditionIds.length === 0) return;
    setError(null);
    setLoadingDoctors(true);
    setPhase('doctors');

    const { success, professionals } = await scheduleService.getProfessionalsByCategories({
      specialtyId,
      categoryIds: selectedConditionIds,
      extra: false,
      userId: loggedInUser?.daisUserId || loggedInUser?.userId,
      authToken: loggedInUser?.authToken || '',
    });

    if (!success) {
      // Fallback: si el endpoint nuevo no devuelve nada, listamos doctoras de
      // la especialidad sin filtrar para no dejar al usuario en cero.
      try {
        const list = await doctorService.getDoctorsBySpecialty(specialtyId);
        setDoctors(list || []);
      } catch (e) {
        setError('No pudimos buscar doctoras. Intenta de nuevo.');
        setDoctors([]);
      }
    } else {
      // Sort: rating desc, then minutes desc
      const sorted = [...professionals].sort((a, b) => {
        if ((b.rating_professional || 0) !== (a.rating_professional || 0)) {
          return (b.rating_professional || 0) - (a.rating_professional || 0);
        }
        return (b.consultation_minutes || 0) - (a.consultation_minutes || 0);
      });
      setDoctors(sorted);
    }
    setLoadingDoctors(false);
  };

  const filteredDoctors = useMemo(() => {
    if (!searchTerm.trim()) return doctors;
    const term = searchTerm.toLowerCase();
    return doctors.filter(d =>
      `${d.name} ${d.last_name || ''}`.toLowerCase().includes(term) ||
      (d.specialty?.name || '').toLowerCase().includes(term)
    );
  }, [doctors, searchTerm]);

  // Duraciones para abrir el modal de schedule.
  const durationsForModal = useMemo(() => {
    if (!selectedSpecialty) return [];
    const items = [];
    (selectedSpecialty.allDurations || []).forEach(d => {
      if (d?.id) items.push({ id: d.id, isExtra: false, durationMinutes: d.duration || null });
    });
    (selectedSpecialty.allExtraDurations || []).forEach(d => {
      if (d?.id) items.push({ id: d.id, isExtra: true, durationMinutes: d.duration || null });
    });
    if (items.length === 0) {
      const fallback = selectedSpecialty.duration || selectedSpecialty.duration_id || 109;
      items.push({ id: fallback, isExtra: false, durationMinutes: null });
    }
    return items;
  }, [selectedSpecialty]);

  const handleViewSchedule = (doctor) => {
    setScheduleModalContext({ doctor, durations: durationsForModal });
    setShowScheduleModal(true);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Fase 1: selección de condiciones
  // ─────────────────────────────────────────────────────────────────────────
  if (phase === 'conditions') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-6 min-h-screen">
        <button onClick={onBack} className="press-scale mb-4 text-daisies flex items-center gap-2 hover:gap-3 transition-all font-medium">
          <ChevronLeft size={20} /> Volver
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-text-primary mb-2 tracking-tight">
            ¿Qué quieres tratar?
          </h2>
          <p className="text-text-secondary text-sm md:text-base">
            Elige una o más condiciones — te mostraremos doctoras especializadas
          </p>
        </div>

        {selectedSpecialty && (
          <div className="flex justify-center mb-5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-tint text-brand-deep rounded-full text-sm font-semibold">
              <span>{selectedSpecialty.name}</span>
            </div>
          </div>
        )}

        {categories.length === 0 ? (
          <div className="text-center py-12 text-text-secondary">
            <AlertCircle className="mx-auto mb-3" size={36} />
            <p>No hay condiciones registradas para esta especialidad todavía.</p>
            <button
              onClick={onBack}
              className="press-scale mt-4 px-4 py-2 bg-daisies text-white rounded-full font-semibold text-sm hover:bg-daisies-hover transition-all"
            >
              Volver
            </button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 justify-center mb-8">
              {categories.map((cond) => {
                const active = selectedConditionIds.includes(cond.id);
                return (
                  <button
                    key={cond.id}
                    onClick={() => toggleCondition(cond.id)}
                    className={`press-scale px-4 py-2.5 rounded-full font-medium text-sm border-2 transition-all ${
                      active
                        ? 'bg-daisies text-white border-daisies shadow-soft'
                        : 'bg-white text-text-primary border-black/10 hover:border-daisies hover:bg-daisies-50'
                    }`}
                  >
                    {cond.name}
                  </button>
                );
              })}
            </div>

            <div className="flex justify-center">
              <button
                onClick={handleSearch}
                disabled={selectedConditionIds.length === 0}
                className={`press-scale px-8 py-3 rounded-full font-semibold transition-all shadow-soft hover:shadow-elevated ${
                  selectedConditionIds.length === 0
                    ? 'bg-surface text-text-secondary cursor-not-allowed'
                    : 'bg-daisies text-white hover:bg-daisies-hover'
                }`}
              >
                Ver doctoras{selectedConditionIds.length > 0 ? ` (${selectedConditionIds.length})` : ''}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Fase 2: lista de doctoras filtradas
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-4xl mx-auto px-3 md:px-4 py-3 md:py-6 min-h-screen">
      <button
        onClick={() => { setPhase('conditions'); setDoctors([]); }}
        className="press-scale mb-3 text-daisies flex items-center gap-1 md:gap-2 hover:gap-2 transition-all text-xs md:text-sm font-medium"
      >
        <ChevronLeft size={16} /> Cambiar condiciones
      </button>

      <div className="mb-4 md:mb-6">
        <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
          <span className="flex items-center justify-center w-11 h-11 rounded-full bg-brand-tint text-brand shrink-0">
            <Stethoscope size={22} aria-hidden="true" />
          </span>
          <div>
            <h1 className="text-lg md:text-2xl font-bold text-brand-deep leading-tight tracking-tight">
              Doctoras para ti
            </h1>
            <p className="text-text-secondary text-xs md:text-sm mt-0.5">
              {doctors.length} {doctors.length === 1 ? 'doctora' : 'doctoras'} • {selectedConditionIds.length} {selectedConditionIds.length === 1 ? 'condición' : 'condiciones'}
            </p>
          </div>
        </div>

        {/* Buscador */}
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={16} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre..."
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-warm-canvas rounded-input focus:outline-none focus:ring-2 focus:ring-brand transition"
          />
        </div>
      </div>

      {loadingDoctors ? (
        <GlobalLoader title="Buscando doctoras" subtitle="Filtrando especialistas según tus condiciones..." overlay={false} />
      ) : error ? (
        <div className="text-center py-10">
          <AlertCircle className="text-red-500 mx-auto mb-3" size={36} />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleSearch}
            className="press-scale px-4 py-2 bg-daisies text-white rounded-full font-semibold text-sm hover:bg-daisies-hover transition-all"
          >
            Reintentar
          </button>
        </div>
      ) : filteredDoctors.length === 0 ? (
        <div className="text-center py-10 bg-surface-card rounded-card">
          <UserRoundSearch className="mx-auto mb-3 text-brand/50" size={40} aria-hidden="true" />
          <h3 className="text-base md:text-lg font-semibold text-brand-deep mb-1 md:mb-2">
            No encontramos doctoras
          </h3>
          <p className="text-text-secondary text-xs md:text-sm mb-3 md:mb-4">
            No hay especialistas registradas para esas condiciones.
          </p>
          {onOndemandRequest && (
            <button
              onClick={() => onOndemandRequest(null)}
              className="press-scale px-4 py-2 bg-daisies-50 text-daisies rounded-full font-semibold text-sm hover:bg-daisies-100 transition-all inline-flex items-center gap-2"
            >
              <Sparkles size={14} /> Proponer SOS
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3 md:space-y-4">
          {filteredDoctors.map(doctor => (
            <DoctorCard
              key={doctor.id}
              doctor={doctor}
              onSelect={() => {}}
              isSelected={false}
              onViewSchedule={handleViewSchedule}
            />
          ))}
        </div>
      )}

      {showScheduleModal && scheduleModalContext && (
        <DoctorScheduleModal
          doctor={scheduleModalContext.doctor}
          durations={scheduleModalContext.durations}
          authToken={loggedInUser?.authToken || ''}
          userId={loggedInUser?.daisUserId || loggedInUser?.userId}
          onSelect={(slot) => {
            onSelect({
              ...slot,
              doctor: scheduleModalContext.doctor,
              professional_ids: slot.professional_ids?.length > 0
                ? slot.professional_ids
                : [{ id: scheduleModalContext.doctor.id }],
              professional_id: scheduleModalContext.doctor.id,
            });
            setShowScheduleModal(false);
          }}
          onClose={() => {
            setShowScheduleModal(false);
            setScheduleModalContext(null);
          }}
          onOndemandRequest={onOndemandRequest}
        />
      )}
    </div>
  );
};

export default ConditionSelection;
